import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { useBoardWorklogs } from "../../../../../reactQuery/boards";
import { setDateRange, setWorkHours } from "../../../../../redux/SearchSlice";
import { getLastSevenDays } from "../../../../../utils";
import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";
import useBoardIdByKey from "../../../../../hooks/useBoardIdByKey";
import { getResponseData } from "../../../../../reactQuery/response";
import {
  parseWorklogDateRangeParams,
} from "../../../../myTask/utils/myTaskUtils";
import { useSearchParamActions } from "../../../../../hooks/useSearchParamActions";
import { matchesWorklogSearch } from "../utils/worklogUtils";

export function useBoardWorklogState() {
  const [byTask, setByTask] = useState(false);
  const { id } = useBoardIdByKey();
  const dispatch = useDispatch();
  const { dateRange, searchText } = useSelector((state) => ({
    dateRange: state.search.dateRange,
    searchText: state.search.searchText,
  }));
  const deferredSearchText = useDeferredValue(searchText);
  const [selectedUserSearch, updateSearchParams] =
    useCustomSearchParams("search");
  const [cardData] = useCustomSearchParams("card_id");
  const [milestone] = useCustomSearchParams("milestone");
  const [searchParams] = useSearchParams();
  const { updateSearchParams: setSearchParams } = useSearchParamActions();
  const selectedUsers = selectedUserSearch?.split(",") || [];

  const { boardWorklog, isLoading } = useBoardWorklogs({
    dateRange,
    id: [id],
    milestone: milestone ? [milestone] : [],
    user: selectedUsers.map((userId) => ({ user_id: userId })),
  });

  const worklogs = useMemo(() => {
    const responseData = getResponseData(boardWorklog, []);

    return responseData.filter((worklog) =>
      matchesWorklogSearch(worklog, deferredSearchText)
    );
  }, [boardWorklog, deferredSearchText]);
  const totalWorkHours = useMemo(
    () =>
      worklogs.reduce(
        (accumulator, currentValue) => accumulator + currentValue.cardlog.hours_spent,
        0
      ),
    [worklogs]
  );

  useEffect(() => {
    dispatch(setWorkHours(totalWorkHours));
  }, [dispatch, totalWorkHours]);

  useEffect(() => {
    const from = searchParams.get("from");
    const days = searchParams.get("days");
    const to = searchParams.get("to");
    const storedDate = searchParams.get("date");
    const defaultDateParams = new URLSearchParams(getLastSevenDays());
    const dateValue = parseWorklogDateRangeParams(
      from || days || to || storedDate
        ? { from, days, to, date: storedDate }
        : {
            from: defaultDateParams.get("from"),
            days: defaultDateParams.get("days"),
          }
    );

    if (!from || !days) {
      setSearchParams([
        { name: "from", value: defaultDateParams.get("from") },
        { name: "days", value: defaultDateParams.get("days") },
        { name: "to", value: "" },
        { name: "date", value: "" },
      ]);
    }

    dispatch(setDateRange(dateValue));
  }, [dispatch, searchParams, setSearchParams, updateSearchParams]);

  const handleByTaskToggle = useCallback(() => {
    setByTask((previousValue) => !previousValue);
  }, []);

  return {
    byTask,
    cardData,
    handleByTaskToggle,
    isLoading,
    totalWorkHours,
    worklogs,
  };
}
